import Button from 'components/Button';
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { logout } from 'slice/userSlice';
import { useDispatch } from 'react-redux';
import { IoPersonCircleSharp, IoLogOutOutline } from 'react-icons/io5';
import { MdContacts, MdDashboard } from 'react-icons/md';
import { FaImages, FaBox, FaGift, FaUsers, FaStar } from 'react-icons/fa';
import { RiOrderPlayLine } from 'react-icons/ri';
import { TbCategory } from 'react-icons/tb';
import { io } from "socket.io-client";
import { toast } from 'react-toastify';
import logo from '../asserts/img/logodry.png'

const socket = io(process.env.REACT_APP_BACKEND, { reconnection: false })

const Layout = ({ children }) => {
  const [toggled, setToggle] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [counts, setCounts] = useState({
    pendingProductOrders: 0,
    pendingGiftOrders: 0,
    newContactEnquiries: 0,
    newUsers: 0,
  });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const audioRef = useRef(null);

  const currentUrl = location.pathname;
  const lastSegment = currentUrl.substring(currentUrl.lastIndexOf('/') + 1);
  const isGalleryActive = lastSegment === 'gallery';
  const isTestimonialsActive = lastSegment === 'testimonials';

  const toggleMenu = (menu) => {
    setOpenMenu(openMenu === menu ? null : menu);
  };

  const logoutHandler = async (e) => {
    e.preventDefault();
    try {
      dispatch(logout());
      navigate('/signin');
    } catch (e) {
      console.log(e);
    }
  };

  const markSeen = (key) => {
    localStorage.setItem(`lastSeen_${key}`, Date.now());
    setCounts((prev) => ({ ...prev, [key]: 0 }));
  };

  useEffect(() => {
    setOpenMenu(null);
    const path = location.pathname;
    const search = location.search;
    if (path.includes('/orderlist') && !search.includes('type=gift')) markSeen('pendingProductOrders');
    if (path.includes('/orderlist') && search.includes('type=gift')) markSeen('pendingGiftOrders');
    if (path.includes('/contactus')) markSeen('newContactEnquiries');
    if (path.includes('/customer')) markSeen('newUsers');
  }, [location.pathname, location.search]);

  const playSound = () => {
    if (audioRef.current) {
      audioRef.current.play().catch((error) => {
        console.warn("Autoplay blocked:", error);
      });
    }
  };

  useEffect(() => {
    socket.on("new-payment", () => {
      setCounts((prev) => ({ ...prev, pendingProductOrders: prev.pendingProductOrders + 1 }));
      toast('New Order Added');
      playSound();
    });

    socket.on("new-contact", () => {
      setCounts((prev) => ({ ...prev, newContactEnquiries: prev.newContactEnquiries + 1 }));
      toast('New Contact Enquiry');
      playSound();
    });

    socket.on("new-customer", () => {
      setCounts((prev) => ({ ...prev, newUsers: prev.newUsers + 1 }));
      toast('New Customer Registered');
      playSound();
    });

    // Fetch initial counts
    const token = localStorage.getItem('token');
    const backendUrl = process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND;
    if (token && backendUrl) {
      fetch(`${backendUrl}admin/new-counts`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then((r) => r.ok ? r.json() : null)
        .then((data) => {
          if (data) {
            // Zero out counts that admin has already seen (within last 5 min)
            const now = Date.now();
            const FIVE_MIN = 5 * 60 * 1000;
            ['pendingProductOrders', 'pendingGiftOrders', 'newContactEnquiries', 'newUsers'].forEach((key) => {
              const lastSeen = localStorage.getItem(`lastSeen_${key}`);
              if (lastSeen && (now - Number(lastSeen)) < FIVE_MIN) {
                data[key] = 0;
              }
            });
            setCounts(data);
          }
        })
        .catch(() => {});
    }

    return () => {
      socket.off("new-payment");
      socket.off("new-contact");
      socket.off("new-customer");
    };
  }, []);

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(path);
  };

  const NavItem = ({ to, icon, label, onClick, isActive: active, badge }) => (
    <li className={`nav-item ${active ? 'active' : ''}`}>
      <Link className="nav-link" to={to || '#'} onClick={onClick}>
        {icon}
        <span className="font-weight-bold">{label}</span>
        {badge && badge}
      </Link>
    </li>
  );

  const SubMenuItem = ({ to, label, isActive: active }) => (
    <Link
      className={`collapse-item ${active ? 'active' : ''}`}
      to={to}
      style={{ textDecoration: 'none' }}
    >
      {label}
    </Link>
  );

  return (
    <div id="wrapper">
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio
        ref={audioRef}
        src="https://assets.mixkit.co/active_storage/sfx/933/933-preview.mp3"
        preload="auto"
      />

      <ul
        className={`navbar-nav sidebar sidebar-dark accordion${toggled ? ' toggled' : ''}`}
        id="accordionSidebar"
      >
        <div className="sidebar-brand">
          <Link to="/dashboard" className="text-decoration-none">
            <img src={logo} alt="TN Dry Fruits" />
          </Link>
        </div>

        <NavItem
          to="/dashboard"
          icon={<MdDashboard />}
          label="Dashboard"
          isActive={isActive('/dashboard')}
        />

        {/* Banner/Gallery */}
        <li className={`nav-item ${isGalleryActive || isActive('/gallery') ? 'active' : ''}`}>
          <Link
            className="nav-link collapsed"
            to="#"
            onClick={() => toggleMenu('gallery')}
          >
            <FaImages />
            <span className="font-weight-bold">Banner</span>
          </Link>
          <div className={`collapse ${openMenu === 'gallery' ? 'show' : ''}`}>
            <div className="collapse-inner rounded">
              <SubMenuItem to="/gallery/add" label="Add Banner" />
              <SubMenuItem to="/gallery" label="Banner List" />
            </div>
          </div>
        </li>

        {/* Categories */}
        <li className={`nav-item ${['categories', 'main-categories', 'sub-categories'].includes(lastSegment) ? 'active' : ''}`}>
          <Link
            className="nav-link collapsed"
            to="#"
            onClick={() => toggleMenu('categories')}
          >
            <TbCategory size={20} />
            <span className="font-weight-bold">Categories</span>
          </Link>
          <div className={`collapse ${openMenu === 'categories' ? 'show' : ''}`}>
            <div className="collapse-inner rounded">
              <SubMenuItem to="/categories/add" label="Add Category" />
              <SubMenuItem to="/categories" label="Category List" />
              <div className="dropdown-divider"></div>
              <SubMenuItem to="/main-categories/add" label="Add Main Category" />
              <SubMenuItem to="/main-categories" label="Main Category List" />
              <div className="dropdown-divider"></div>
              <SubMenuItem to="/sub-categories/add" label="Add Sub Category" />
              <SubMenuItem to="/sub-categories" label="Sub Category List" />
            </div>
          </div>
        </li>

        {/* Products */}
        <li className={`nav-item ${lastSegment === 'products' || lastSegment === 'add' && currentUrl.includes('/products/') ? 'active' : ''}`}>
          <Link
            className="nav-link collapsed"
            to="#"
            onClick={() => toggleMenu('product')}
          >
            <FaBox />
            <span className="font-weight-bold">Products</span>
          </Link>
          <div className={`collapse ${openMenu === 'product' ? 'show' : ''}`}>
            <div className="collapse-inner rounded">
              <SubMenuItem to="/products/add" label="Add Product" />
              <SubMenuItem to="/products" label="Product List" />
            </div>
          </div>
        </li>

        {/* Gift Products */}
        <li className={`nav-item ${lastSegment === 'gift-products' ? 'active' : ''}`}>
          <Link
            className="nav-link collapsed"
            to="#"
            onClick={() => toggleMenu('gift-products')}
          >
            <FaGift />
            <span className="font-weight-bold">Gift Products</span>
          </Link>
          <div className={`collapse ${openMenu === 'gift-products' ? 'show' : ''}`}>
            <div className="collapse-inner rounded">
              <SubMenuItem to="/gift-products/add" label="Add Gift Product" />
              <SubMenuItem to="/gift-products" label="Gift Product List" />
            </div>
          </div>
        </li>

        {/* Product Orders */}
        <NavItem
          to="/orderlist?type=product"
          icon={<RiOrderPlayLine />}
          label={
            <>
              Product Orders
              {counts.pendingProductOrders > 0 && (
                <span className="badge badge-danger ml-2">{counts.pendingProductOrders}</span>
              )}
            </>
          }
          isActive={lastSegment === 'orderlist' && !location.search.includes('type=gift')}
        />

        {/* Giftbox Orders */}
        <NavItem
          to="/orderlist?type=gift"
          icon={<FaGift />}
          label={
            <>
              Giftbox Orders
              {counts.pendingGiftOrders > 0 && (
                <span className="badge badge-danger ml-2">{counts.pendingGiftOrders}</span>
              )}
            </>
          }
          isActive={lastSegment === 'orderlist' && location.search.includes('type=gift')}
        />

        {/* Users */}
        <li className={`nav-item ${lastSegment === 'users' || lastSegment === 'customer' ? 'active' : ''}`}>
          <Link
            className="nav-link collapsed"
            to="#"
            onClick={() => toggleMenu('users')}
          >
            <FaUsers />
            <span className="font-weight-bold">Users</span>
            {counts.newUsers > 0 && (
              <span className="badge badge-danger ml-2">{counts.newUsers}</span>
            )}
          </Link>
          <div className={`collapse ${openMenu === 'users' ? 'show' : ''}`}>
            <div className="collapse-inner rounded">
              <SubMenuItem to="/customer" label="User List" />
            </div>
          </div>
        </li>

        {/* Contact Us */}
        <li className={`nav-item ${lastSegment === 'contactus' ? 'active' : ''}`}>
          <Link
            className="nav-link collapsed"
            to="#"
            onClick={() => toggleMenu('contactus')}
          >
            <MdContacts />
            <span className="font-weight-bold">Contact Us</span>
            {counts.newContactEnquiries > 0 && (
              <span className="badge badge-danger ml-2">{counts.newContactEnquiries}</span>
            )}
          </Link>
          <div className={`collapse ${openMenu === 'contactus' ? 'show' : ''}`}>
            <div className="collapse-inner rounded">
              <SubMenuItem to="/contactus" label="Contact Us List" />
            </div>
          </div>
        </li>

        {/* Testimonials */}
        <li className={`nav-item ${isTestimonialsActive ? 'active' : ''}`}>
          <Link
            className="nav-link collapsed"
            to="#"
            onClick={() => toggleMenu('testimonials')}
          >
            <FaStar />
            <span className="font-weight-bold">Testimonials</span>
          </Link>
          <div className={`collapse ${openMenu === 'testimonials' ? 'show' : ''}`}>
            <div className="collapse-inner rounded">
              <SubMenuItem to="/testimonials/add" label="Add Testimonial" />
              <SubMenuItem to="/testimonials" label="Testimonial List" />
            </div>
          </div>
        </li>

        {/* Hidden menus - accessible via direct URL */}
      </ul>

      <div id="content-wrapper" className="d-flex flex-column">
        <div id="content">
          <nav className="navbar navbar-expand navbar-light topbar static-top">
            <button
              id="sidebarToggleTop"
              onClick={() => setToggle(!toggled)}
              className="btn btn-link d-md-none rounded-circle mr-3"
            >
              <i className="fa fa-bars"></i>
            </button>

            <div className="d-none d-md-flex align-items-center">
              <span className="brand">Admin Panel</span>
            </div>

            <ul className="navbar-nav ml-auto">
              <li className="nav-item dropdown no-arrow">
                <Link
                  className="nav-link dropdown-toggle"
                  to="#"
                  id="userDropdown"
                  role="button"
                  data-toggle="dropdown"
                  aria-haspopup="true"
                  aria-expanded="false"
                >
                  <span className="mr-2 d-none d-lg-inline admintext">Admin</span>
                  <IoPersonCircleSharp size={22} style={{ color: 'var(--accent)' }} />
                </Link>
                <div className="dropdown-menu dropdown-menu-right shadow animated--grow-in" aria-labelledby="userDropdown">
                  <div className="dropdown-divider"></div>
                  <Button
                    className="dropdown-item d-flex align-items-center"
                    text={
                      <>
                        <IoLogOutOutline size={18} className="mr-2" />
                        Logout
                      </>
                    }
                    onclickfun={logoutHandler}
                  />
                </div>
              </li>
            </ul>
          </nav>

          {children}
        </div>
      </div>
    </div>
  );
};

export default Layout;
